/**
 * StudioAssets — PPT/PDF Asset Module.
 * Drag-drop upload, replace as new version, list, preview (browser-native),
 * version history with download, soft-delete.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Upload, FileText, Plus, RefreshCw, Loader2, Download, Trash, Eye, X,
  History, Layers, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./studio.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

function fmtBytes(n) {
  if (!n) return "—";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + " MB";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + " KB";
  return `${n} B`;
}

export default function StudioAssets() {
  const [channels, setChannels] = useState([]);
  const [chFilter, setChFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [contentFilter, setContentFilter] = useState(""); // filter assets by linked task
  const [contentList, setContentList] = useState([]);      // tasks available for the picked channel
  const [attachToContent, setAttachToContent] = useState(""); // upload target
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [versionsAsset, setVersionsAsset] = useState(null);
  const fileInputRef = useRef(null);

  const loadChannels = async () => {
    const r = await axios.get(`${API}/api/studio/channels`, { withCredentials: true });
    const all = [...(r.data?.owned || []), ...(r.data?.shared || [])];
    setChannels(all);
    if (!chFilter && all[0]) setChFilter(all[0].channel_id);
  };

  const loadContentList = async () => {
    if (!chFilter) { setContentList([]); return; }
    try {
      const r = await axios.get(`${API}/api/studio/content/by-channel/${chFilter}`, { withCredentials: true });
      setContentList(r.data?.items || r.data || []);
    } catch { setContentList([]); }
  };

  const loadAssets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (chFilter) params.set("channel_id", chFilter);
      if (kindFilter) params.set("kind", kindFilter);
      if (contentFilter) params.set("content_id", contentFilter);
      const r = await axios.get(`${API}/api/studio/asset-store/list?${params}`, { withCredentials: true });
      setAssets(r.data?.assets || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load assets");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadChannels(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    if (chFilter) { loadAssets(); loadContentList(); }
    /* eslint-disable-next-line */
  }, [chFilter, kindFilter, contentFilter]);

  const doUpload = async (file) => {
    if (!chFilter) { toast.error("Pick a channel first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("channel_id", chFilter);
      if (attachToContent) fd.append("content_id", attachToContent);
      fd.append("file", file);
      fd.append("title", file.name);
      await axios.post(`${API}/api/studio/asset-store/upload`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const ct = contentList.find((c) => (c._id || c.id) === attachToContent);
      toast.success(`Uploaded ${file.name}${ct ? ` → ${ct.title}` : ""}`);
      loadAssets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f) doUpload(f);
  };

  const replaceAsset = async (assetId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.put(`${API}/api/studio/asset-store/${assetId}/replace`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Replaced — new version created");
      loadAssets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Replace failed");
    }
  };

  const softDelete = async (assetId) => {
    if (!window.confirm("Move this asset to trash?")) return;
    try {
      await axios.post(`${API}/api/studio/asset-store/${assetId}/soft-delete`, {}, { withCredentials: true });
      toast.success("Moved to trash");
      loadAssets();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const downloadUrl = (assetId, version) =>
    `${API}/api/studio/asset-store/${assetId}${version ? `/v/${version}` : "/download"}`;

  return (
    <>
      <Header />
      <div className="studio-shell">
        <div className="studio-hero" style={{ paddingBottom: 16 }}>
          <span className="studio-eyebrow"><Layers size={12}/> PPT / PDF Asset Module</span>
          <h1 className="studio-h1">
            Presentation assets, <span className="studio-h1-accent">versioned & previewable.</span>
          </h1>
          <p className="studio-sub">
            Upload PPT/PPTX/PDF for each channel. Replace creates a new version automatically — old versions stay downloadable forever.
          </p>
          <div className="studio-hero-cta">
            <select className="studio-input" value={chFilter} onChange={(e) => setChFilter(e.target.value)} data-testid="assets-channel">
              <option value="">All channels</option>
              {channels.map((c) => <option key={c.channel_id} value={c.channel_id}>{c.title}</option>)}
            </select>
            <select className="studio-input" value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="assets-kind">
              <option value="">All types</option>
              <option value="pdf">PDF</option>
              <option value="pptx">PPTX</option>
              <option value="ppt">PPT</option>
            </select>
            <select
              className="studio-input"
              value={contentFilter}
              onChange={(e) => setContentFilter(e.target.value)}
              data-testid="assets-content-filter"
              style={{ minWidth: 220 }}
              disabled={!contentList.length}
            >
              <option value="">All video tasks</option>
              {contentList.map((c) => (
                <option key={c._id || c.id} value={c._id || c.id}>
                  [{c.stage || "idea"}] {(c.title || "Untitled").slice(0, 50)}
                </option>
              ))}
            </select>
            <button className="studio-btn-ghost" onClick={loadAssets} disabled={loading} data-testid="assets-refresh">
              {loading ? <Loader2 size={14} className="ac-spin"/> : <RefreshCw size={14}/>} Refresh
            </button>
            <button className="studio-btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading || !chFilter} data-testid="assets-upload-btn">
              {uploading ? <Loader2 size={14} className="ac-spin"/> : <Plus size={14}/>} Upload
            </button>
            <input
              type="file" ref={fileInputRef} accept=".pdf,.ppt,.pptx,application/pdf"
              onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])}
              style={{ display: "none" }} data-testid="assets-file-input"
            />
          </div>
        </div>

        <div
          className={`studio-card ${drag ? "studio-card--drag" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          style={{
            padding: 24, textAlign: "center", marginBottom: 18,
            border: drag ? "2px dashed var(--st-purple)" : "2px dashed var(--st-line)",
            background: drag ? "rgba(167,139,250,0.06)" : undefined,
          }}
          data-testid="assets-dropzone"
        >
          <Upload size={28} style={{ color: "var(--st-purple)" }}/>
          <p style={{ margin: "8px 0 0", color: "var(--st-text-soft)" }}>
            <strong>Drag & drop</strong> a PDF/PPT/PPTX here — or click <em>Upload</em> above.
          </p>
          <small className="studio-muted">Max 50MB per file</small>
          {contentList.length > 0 && (
            <div style={{ marginTop: 14, display: "flex", justifyContent: "center", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <small className="studio-muted">Attach to video task:</small>
              <select
                className="studio-input"
                value={attachToContent}
                onChange={(e) => setAttachToContent(e.target.value)}
                data-testid="assets-attach-target"
                style={{ minWidth: 260, padding: "6px 10px", fontSize: 12 }}
              >
                <option value="">— Channel-level (no task) —</option>
                {contentList.map((c) => (
                  <option key={c._id || c.id} value={c._id || c.id}>
                    [{c.stage || "idea"}] {(c.title || "Untitled").slice(0, 60)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {loading && (
          <div className="studio-grid">
            {[1,2,3].map((i) => <div key={i} className="studio-skeleton studio-sk-card" style={{ height: 140 }}/>)}
          </div>
        )}

        {!loading && assets.length === 0 && (
          <div className="studio-empty" data-testid="assets-empty">
            <div className="studio-empty-icon"><FileText size={36}/></div>
            <h2>No assets yet</h2>
            <p>Drop a PDF or PPT above to get started.</p>
          </div>
        )}

        {!loading && assets.length > 0 && (
          <div className="studio-grid" data-testid="assets-list">
            {assets.map((a) => (
              <div key={a.asset_id} className="studio-channel-card" data-testid={`asset-${a.asset_id}`}>
                <div className="studio-channel-head">
                  <div className="studio-channel-meta" style={{ flex: 1 }}>
                    <h3 style={{ fontSize: "1rem" }}>{a.title}</h3>
                    <p>{a.filename} · {fmtBytes(a.size_bytes)}</p>
                  </div>
                  <span className={`studio-role studio-role--${a.kind === "pdf" ? "designer" : "creator"}`}>
                    {a.kind?.toUpperCase()}
                  </span>
                </div>
                <div style={{ padding: "8px 16px", fontSize: 12, color: "var(--st-text-muted)" }}>
                  Version <strong style={{ color: "var(--st-purple)" }}>v{a.current_version}</strong> · {a.version_count} total ·
                  {" "}by {a.uploaded_by_name || "—"}
                </div>
                {a.content_id && (
                  <div style={{ padding: "0 16px 8px", fontSize: 11 }}>
                    <span className="studio-pill studio-pill--sm" data-testid={`asset-task-${a.asset_id}`}>
                      🎬 {(contentList.find((c) => (c._id || c.id) === a.content_id)?.title || a.content_id.slice(0, 8)).slice(0, 40)}
                    </span>
                  </div>
                )}
                <div className="studio-channel-foot" style={{ gap: 6, flexWrap: "wrap" }}>
                  <button className="studio-btn-ghost" onClick={() => setPreviewAsset(a)} data-testid={`asset-preview-${a.asset_id}`}>
                    <Eye size={12}/> Preview
                  </button>
                  <a className="studio-btn-ghost" href={downloadUrl(a.asset_id)} download data-testid={`asset-download-${a.asset_id}`}>
                    <Download size={12}/> Download
                  </a>
                  <ReplaceBtn assetId={a.asset_id} onReplace={replaceAsset}/>
                  <button className="studio-btn-ghost" onClick={() => setVersionsAsset(a)} data-testid={`asset-versions-${a.asset_id}`}>
                    <History size={12}/> Versions
                  </button>
                  <button className="studio-btn-ghost" onClick={() => softDelete(a.asset_id)} data-testid={`asset-delete-${a.asset_id}`} style={{ color: "#ef4444" }}>
                    <Trash size={12}/>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {previewAsset && <PreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)}/>}
      {versionsAsset && <VersionsModal asset={versionsAsset} onClose={() => setVersionsAsset(null)}/>}

      <Footer />
    </>
  );
}

function ReplaceBtn({ assetId, onReplace }) {
  const ref = useRef(null);
  return (
    <>
      <button className="studio-btn-ghost" onClick={() => ref.current?.click()} data-testid={`asset-replace-${assetId}`}>
        <RefreshCw size={12}/> Replace
      </button>
      <input
        type="file" ref={ref} accept=".pdf,.ppt,.pptx" style={{ display: "none" }}
        onChange={(e) => { if (e.target.files?.[0]) { onReplace(assetId, e.target.files[0]); e.target.value = ""; } }}
      />
    </>
  );
}

function PreviewModal({ asset, onClose }) {
  const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/studio/asset-store/${asset.asset_id}/download`;
  return (
    <div className="studio-vid-overlay" onClick={onClose} data-testid="asset-preview-modal">
      <div className="studio-vid-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1000 }}>
        <header>
          <h3>{asset.title} · v{asset.current_version}</h3>
          <button className="studio-btn-ghost" onClick={onClose}><X size={14}/></button>
        </header>
        <div className="studio-vid-embed" style={{ aspectRatio: "auto", height: "70vh" }}>
          {asset.kind === "pdf" ? (
            <iframe src={url} title={asset.title} style={{ width: "100%", height: "100%", border: 0 }}/>
          ) : (
            <div style={{ padding: 40, textAlign: "center", color: "var(--st-text-soft)" }}>
              <FileText size={48}/>
              <p style={{ marginTop: 12 }}>
                Browser cannot preview PPT/PPTX inline. Download to view.
              </p>
              <a className="studio-btn-primary" href={url} download style={{ marginTop: 12 }}>
                <Download size={14}/> Download
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionsModal({ asset, onClose }) {
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL || ''}/api/studio/asset-store/${asset.asset_id}`, { withCredentials: true })
      .then((r) => setDetail(r.data))
      .catch(() => {});
  }, [asset.asset_id]);

  const url = (v) => `${process.env.REACT_APP_BACKEND_URL || ''}/api/studio/asset-store/${asset.asset_id}/v/${v}`;

  return (
    <div className="studio-vid-overlay" onClick={onClose} data-testid="asset-versions-modal">
      <div className="studio-vid-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <header>
          <h3><History size={16} style={{ verticalAlign: "-3px", marginRight: 6 }}/> Version history · {asset.title}</h3>
          <button className="studio-btn-ghost" onClick={onClose}><X size={14}/></button>
        </header>
        <div style={{ padding: 18, maxHeight: "70vh", overflowY: "auto" }}>
          {!detail ? <Loader2 size={20} className="ac-spin"/> : (
            <table className="studio-pipe-table" style={{ minWidth: 0 }}>
              <thead><tr><th>Version</th><th>Size</th><th>Uploaded by</th><th>At</th><th>Notes</th><th></th></tr></thead>
              <tbody>
                {(detail.versions || []).slice().reverse().map((v) => (
                  <tr key={v.version} data-testid={`asset-version-row-${v.version}`}>
                    <td><strong>v{v.version}</strong>{v.version === detail.current_version && <span className="stage-pill" style={{ marginLeft: 6 }}>current</span>}</td>
                    <td>{fmtBytes(v.size_bytes)}</td>
                    <td>{v.uploaded_by_name || "—"}</td>
                    <td>{v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : "—"}</td>
                    <td>{v.notes || "—"}</td>
                    <td>
                      <a className="studio-btn-ghost" href={url(v.version)} download data-testid={`asset-version-download-${v.version}`} style={{ padding: "4px 8px" }}>
                        <Download size={12}/>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
